import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useCallback, useEffect, useMemo, useState } from "react";

export type LengthNumberInputProps = {
  id?: string;
  value: number; // Value in millimeters
  onChange?: (value: number) => void;
  unit?: LengthUnit; // Fixed unit if provided, does not allow other selection
  defaultUnit?: LengthUnit; // default displayed unit, does allow selection of other units
  className?: string;
  disabled?: boolean;
  locale?: string; // Optional locale, defaults to 'de-DE' for ',' as decimal separator
};

export function LengthNumberInput({
  id,
  value,
  onChange,
  unit,
  defaultUnit,
  className = "",
  disabled = false,
  locale = "de-DE",
}: LengthNumberInputProps) {
  const [selectedUnit, setSelectedUnit] = useState<LengthUnit>(
    defaultUnit || LengthUnit.Centimeters,
  );
  const [inputValue, setInputValue] = useState<string>("0");

  const activeUnit = unit || selectedUnit;

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [locale],
  );

  const formatLocaleNumber = useCallback(
    (number: number) => formatter.format(Number.isNaN(number) ? 0 : number),
    [formatter],
  );

  const parseLocaleNumber = useCallback(
    (input: string) => {
      const separator = formatter.format(1.1).charAt(1); // Get decimal separator
      const normalized = input.replace(separator, ".");
      return parseFloat(normalized);
    },
    [formatter],
  );

  const convertFromMillimeters = useCallback((mm: number, u: LengthUnit) => {
    switch (u) {
      case LengthUnit.Centimeters:
        return mm / 10;
      case LengthUnit.Meters:
        return mm / 1000;
      case LengthUnit.Kilometers:
        return mm / 1e6;
      default:
        return mm;
    }
  }, []);

  const convertToMillimeters = useCallback((val: number, u: LengthUnit) => {
    switch (u) {
      case LengthUnit.Centimeters:
        return val * 10;
      case LengthUnit.Meters:
        return val * 1000;
      case LengthUnit.Kilometers:
        return val * 1e6;
      default:
        return val;
    }
  }, []);

  useEffect(() => {
    setInputValue(
      formatLocaleNumber(convertFromMillimeters(value, activeUnit)),
    );
  }, [value, activeUnit, formatLocaleNumber, convertFromMillimeters]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleBlurOrEnter = () => {
    const parsedValue = parseLocaleNumber(inputValue);
    if (!Number.isNaN(parsedValue)) {
      const millimeters = convertToMillimeters(parsedValue, activeUnit);
      const roundedValue = convertFromMillimeters(millimeters, activeUnit);
      setInputValue(formatLocaleNumber(roundedValue));
      if (onChange) {
        onChange(millimeters);
      }
    } else {
      setInputValue(
        formatLocaleNumber(convertFromMillimeters(value, activeUnit)),
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleBlurOrEnter();
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        pattern="[0-9]*[.,]?[0-9]*"
        className="h-8 w-24 text-end"
        value={inputValue}
        disabled={disabled}
        onChange={handleInputChange}
        onBlur={handleBlurOrEnter}
        onKeyDown={handleKeyDown}
      />
      {unit ? (
        <span className="flex h-8 items-center px-1">
          {LengthUnitUtil.toString(unit)}
        </span>
      ) : (
        <Select
          value={selectedUnit}
          defaultValue={defaultUnit || LengthUnit.Centimeters}
          onValueChange={(val) => setSelectedUnit(val as LengthUnit)}
        >
          <SelectTrigger className="h-8 w-fit">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={LengthUnit.Millimeters}>
              {LengthUnitUtil.toString(LengthUnit.Millimeters)}
            </SelectItem>
            <SelectItem value={LengthUnit.Centimeters}>
              {LengthUnitUtil.toString(LengthUnit.Centimeters)}
            </SelectItem>
            <SelectItem value={LengthUnit.Meters}>
              {LengthUnitUtil.toString(LengthUnit.Meters)}
            </SelectItem>
            <SelectItem value={LengthUnit.Kilometers}>
              {LengthUnitUtil.toString(LengthUnit.Kilometers)}
            </SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

export enum LengthUnit {
  Millimeters = "millimeters",
  Centimeters = "centtimeters",
  Meters = "meters",
  Kilometers = "kilometers",
}

export class LengthUnitUtil {
  private static readonly descriptions: {
    [key in LengthUnit]: string;
  } = {
    [LengthUnit.Millimeters]: "Millimeter",
    [LengthUnit.Centimeters]: "Zentimeter",
    [LengthUnit.Meters]: "Meter",
    [LengthUnit.Kilometers]: "Kilometer",
  };

  private static readonly abbreviations: {
    [key in LengthUnit]: string;
  } = {
    [LengthUnit.Millimeters]: "mm",
    [LengthUnit.Centimeters]: "cm",
    [LengthUnit.Meters]: "m",
    [LengthUnit.Kilometers]: "km",
  };

  static toString(type: LengthUnit): string {
    return this.abbreviations[type];
  }

  static toLongString(type: LengthUnit): string {
    return this.descriptions[type] || "Unknown";
  }
}
